import { useRef, useState } from "react";
import { MessageCircleQuestion, Send, Loader2, Sparkles } from "lucide-react";
import api from "../../lib/api";

/* "Ask about this trip" — AI answers only from the traveler's stored trip data. */
export default function AskMyTrip({ tripId }) {
  const [messages, setMessages] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const boxRef = useRef(null);

  const ask = async (text) => {
    const question = (text || q).trim();
    if (!question || busy) return;
    const history = messages.map((m) => ({ role: m.role, text: m.text }));
    setMessages((m) => [...m, { role: "user", text: question }]);
    setQ(""); setBusy(true);
    try {
      const { data } = await api.post(`/trips/${tripId}/ask`, { question, history }, { timeout: 60000 });
      setMessages((m) => [...m, { role: "assistant", text: data.answer }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", text: "Something went wrong — please try again." }]);
    } finally {
      setBusy(false);
      setTimeout(() => boxRef.current?.scrollTo({ top: boxRef.current.scrollHeight, behavior: "smooth" }), 100);
    }
  };

  const chips = ["What's my PNR?", "When do I arrive?", "What's my hotel?", "Who is traveling with me?", "What documents do I have?"];

  return (
    <section className="mb-10" data-testid="ask-my-trip">
      <h2 className="font-heading font-900 text-violet-700 text-2xl flex items-center gap-2 mb-2">
        <MessageCircleQuestion className="w-6 h-6" /> Ask about this trip
      </h2>
      <p className="text-sm text-muted-foreground mb-3">Answers come only from what you've stored in this trip — nothing invented.</p>
      <div className="border border-zinc-300 bg-white">
        {messages.length > 0 && (
          <div ref={boxRef} className="max-h-72 overflow-y-auto p-4 space-y-3 border-b border-zinc-200" data-testid="ask-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-primary text-white" : "bg-violet-50 border border-violet-200 text-zinc-800"}`}
                  data-testid={m.role === "user" ? "ask-msg-user" : "ask-msg-assistant"}>
                  {m.role === "assistant" && <Sparkles className="w-3.5 h-3.5 inline mr-1 text-violet-500" />}{m.text}
                </div>
              </div>
            ))}
            {busy && <div className="text-sm text-muted-foreground animate-pulse flex items-center gap-1"><Loader2 className="w-4 h-4 animate-spin" /> Checking your trip…</div>}
          </div>
        )}
        <div className="p-3">
          <div className="flex flex-wrap gap-1.5 mb-2">
            {chips.map((c) => (
              <button key={c} onClick={() => ask(c)} disabled={busy} data-testid="ask-chip"
                className="px-2.5 py-1 text-xs border border-violet-200 text-violet-700 hover:bg-violet-50 disabled:opacity-50">{c}</button>
            ))}
          </div>
          <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="flex gap-2">
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="ask-input"
              placeholder='Ask anything — "what seat am I in?", "when do I land in Paris?"'
              className="flex-1 border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-violet-500" />
            <button type="submit" disabled={busy || !q.trim()} data-testid="ask-send"
              className="px-4 py-2 bg-violet-600 text-white text-sm font-bold hover:bg-violet-700 disabled:opacity-50">
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
